import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TagInput } from '@/components/common/TagInput'
import { useSkuAttributeCategories } from '@/hooks/useSkuAttributes'
import { toast } from '@/stores/useToastStore'
import type {
  Attribute,
  AttributeFormData,
  AttributeType,
} from '@/types/attribute'

const attributeFormSchema = z.object({
  category: z.string().trim().min(1, 'Category is required'),
  subcategory: z.string().trim().min(1, 'Subcategory is required'),
  category_id: z.string().nullable().optional(),
  subcategory_id: z.string().nullable().optional(),
  attribute_type: z.enum([
    'TEXT',
    'NUMBER',
    'DATE',
    'DROPDOWN',
    'BOOLEAN',
  ] as const),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().min(1, 'Description is required'),
  required: z.boolean(),
  locked: z.boolean(),
  defined_values: z.array(z.string()),
})

type AttributeFormValues = z.infer<typeof attributeFormSchema>

interface AttributeFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  attribute: Attribute | null
  onSubmit: (data: AttributeFormData) => void | Promise<void>
}

const ATTRIBUTE_TYPE_OPTIONS: { value: AttributeType; label: string }[] = [
  { value: 'TEXT', label: 'TEXT' },
  { value: 'NUMBER', label: 'NUMBER' },
  { value: 'DATE', label: 'DATE' },
  { value: 'DROPDOWN', label: 'DROPDOWN' },
  { value: 'BOOLEAN', label: 'BOOLEAN' },
]

const DEFAULT_VALUES: AttributeFormValues = {
  category: '',
  subcategory: '',
  category_id: null,
  subcategory_id: null,
  attribute_type: 'TEXT',
  name: '',
  description: '',
  required: false,
  locked: false,
  defined_values: [],
}

type CategoryOption = {
  id: string | null
  name: string
}

export function AttributeFormDialog({
  open,
  onOpenChange,
  attribute,
  onSubmit,
}: AttributeFormDialogProps) {
  const isEdit = attribute !== null
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false)
  const [isSubcategoryMenuOpen, setIsSubcategoryMenuOpen] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<AttributeFormValues>({
    resolver: zodResolver(attributeFormSchema),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (open) {
      if (attribute) {
        reset({
          category: attribute.category,
          subcategory: attribute.subcategory,
          category_id: attribute.category_id ?? null,
          subcategory_id: attribute.subcategory_id ?? null,
          attribute_type: attribute.attribute_type ?? 'TEXT',
          name: attribute.name,
          description: attribute.description,
          required:
            typeof attribute.required === 'boolean'
              ? attribute.required
              : false,
          locked:
            typeof attribute.locked === 'boolean' ? attribute.locked : false,
          defined_values: attribute.defined_values,
        })
      } else {
        reset(DEFAULT_VALUES)
      }
    } else {
      reset(DEFAULT_VALUES)
      clearErrors()
      setIsCategoryMenuOpen(false)
      setIsSubcategoryMenuOpen(false)
    }
  }, [open, attribute, reset, clearErrors])

  const currentAttributeType = watch('attribute_type')
  const currentCategoryId = watch('category_id')
  const currentCategoryName = watch('category')
  const currentSubcategoryId = watch('subcategory_id')
  const currentSubcategoryName = watch('subcategory')
  const currentRequired = watch('required')
  const currentLocked = watch('locked')
  const attributeTypeValue =
    currentAttributeType && currentAttributeType.length > 0
      ? currentAttributeType
      : 'TEXT'

  useEffect(() => {
    if (!open) {
      return
    }

    if (!currentAttributeType || currentAttributeType.length === 0) {
      setValue('attribute_type', 'TEXT', { shouldValidate: false })
      clearErrors('attribute_type')
    }
  }, [open, currentAttributeType, setValue, clearErrors])

  const { data: categories = [] } = useSkuAttributeCategories(null)
  const { data: fetchedSubcategories = [] } = useSkuAttributeCategories(
    currentCategoryId,
    Boolean(currentCategoryId)
  )
  const subcategories = useMemo(
    () => (currentCategoryId ? fetchedSubcategories : []),
    [currentCategoryId, fetchedSubcategories]
  )

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    const baseOptions = categories.map(category => ({
      id: category.id,
      name: category.name,
    }))

    if (
      currentCategoryId &&
      !baseOptions.some(option => option.id === currentCategoryId)
    ) {
      baseOptions.push({
        id: currentCategoryId,
        name: currentCategoryName,
      })
    }

    return baseOptions
  }, [categories, currentCategoryId, currentCategoryName])

  const subcategoryOptions = useMemo<CategoryOption[]>(() => {
    if (!currentCategoryId) {
      return []
    }

    const baseOptions = subcategories.map(subcategory => ({
      id: subcategory.id,
      name: subcategory.name,
    }))

    if (
      currentSubcategoryId &&
      !baseOptions.some(option => option.id === currentSubcategoryId)
    ) {
      baseOptions.push({
        id: currentSubcategoryId,
        name: currentSubcategoryName,
      })
    }

    return baseOptions
  }, [
    currentCategoryId,
    subcategories,
    currentSubcategoryId,
    currentSubcategoryName,
  ])

  const filteredCategoryOptions = useMemo(() => {
    const normalized = currentCategoryName.trim().toLowerCase()
    if (!normalized) {
      return categoryOptions
    }

    return categoryOptions.filter(option =>
      option.name.toLowerCase().includes(normalized)
    )
  }, [categoryOptions, currentCategoryName])

  const filteredSubcategoryOptions = useMemo(() => {
    const normalized = currentSubcategoryName.trim().toLowerCase()
    if (!normalized) {
      return subcategoryOptions
    }

    return subcategoryOptions.filter(option =>
      option.name.toLowerCase().includes(normalized)
    )
  }, [subcategoryOptions, currentSubcategoryName])

  const handleCategoryInputChange = (value: string) => {
    const normalizedValue = value.trim().toLowerCase()
    const matchedOption = categoryOptions.find(
      option => option.name.trim().toLowerCase() === normalizedValue
    )

    setValue('category', value, { shouldValidate: true })
    setValue('category_id', matchedOption?.id ?? null, { shouldValidate: true })
    setValue('subcategory', '', { shouldValidate: true })
    setValue('subcategory_id', null, { shouldValidate: true })
    setIsCategoryMenuOpen(filteredCategoryOptions.length > 0)
  }

  const handleSubcategoryInputChange = (value: string) => {
    const normalizedValue = value.trim().toLowerCase()
    const matchedOption = subcategoryOptions.find(
      option => option.name.trim().toLowerCase() === normalizedValue
    )

    setValue('subcategory', value, { shouldValidate: true })
    setValue('subcategory_id', matchedOption?.id ?? null, {
      shouldValidate: true,
    })
    setIsSubcategoryMenuOpen(filteredSubcategoryOptions.length > 0)
  }

  const handleCategoryOptionSelect = (option: CategoryOption) => {
    setValue('category', option.name, { shouldValidate: true })
    setValue('category_id', option.id, { shouldValidate: true })
    setValue('subcategory', '', { shouldValidate: true })
    setValue('subcategory_id', null, { shouldValidate: true })
    setIsCategoryMenuOpen(false)
  }

  const handleSubcategoryOptionSelect = (option: CategoryOption) => {
    setValue('subcategory', option.name, { shouldValidate: true })
    setValue('subcategory_id', option.id, { shouldValidate: true })
    setIsSubcategoryMenuOpen(false)
  }

  const handleFormSubmit = async (data: AttributeFormValues) => {
    try {
      if (data.attribute_type === 'DROPDOWN') {
        const normalizedDefinedValues = data.defined_values
          .map(value => value.trim())
          .filter(Boolean)

        if (normalizedDefinedValues.length === 0) {
          const message = 'Defined Values is required for DROPDOWN type'
          setError('defined_values', {
            type: 'manual',
            message,
          })
          toast.error(message)
          return
        }

        clearErrors('defined_values')
      }

      const submitData = {
        ...data,
        defined_values:
          data.attribute_type === 'DROPDOWN'
            ? data.defined_values.map(value => value.trim()).filter(Boolean)
            : data.defined_values,
        category_id: data.category_id || undefined,
        subcategory_id: data.subcategory_id || undefined,
      } as unknown as AttributeFormData

      await onSubmit(submitData)
      onOpenChange(false)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save attribute'
      toast.error(message)
      throw error
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-125">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Attribute' : 'Add New Attribute'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the attribute details below.'
              : 'Fill in the details to create a new attribute.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <div className="relative">
              <Input
                id="category"
                value={currentCategoryName}
                disabled={isEdit}
                autoComplete="off"
                placeholder="Type category"
                onFocus={() => setIsCategoryMenuOpen(true)}
                onBlur={() => setIsCategoryMenuOpen(false)}
                onChange={event =>
                  handleCategoryInputChange(event.target.value)
                }
              />

              {isCategoryMenuOpen &&
                !isEdit &&
                filteredCategoryOptions.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
                    {filteredCategoryOptions.map(option => (
                      <button
                        key={option.id ?? option.name}
                        type="button"
                        className="w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        onMouseDown={event => {
                          event.preventDefault()
                          handleCategoryOptionSelect(option)
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                )}
            </div>

            {errors.category && (
              <p className="text-sm text-destructive">
                {errors.category.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subcategory">Subcategory</Label>
            <div className="relative">
              <Input
                id="subcategory"
                value={currentSubcategoryName}
                disabled={isEdit}
                autoComplete="off"
                placeholder="Type subcategory"
                onFocus={() => setIsSubcategoryMenuOpen(true)}
                onBlur={() => setIsSubcategoryMenuOpen(false)}
                onChange={event =>
                  handleSubcategoryInputChange(event.target.value)
                }
              />

              {isSubcategoryMenuOpen &&
                !isEdit &&
                filteredSubcategoryOptions.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
                    {filteredSubcategoryOptions.map(option => (
                      <button
                        key={option.id ?? option.name}
                        type="button"
                        className="w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        onMouseDown={event => {
                          event.preventDefault()
                          handleSubcategoryOptionSelect(option)
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                )}
            </div>

            {errors.subcategory && (
              <p className="text-sm text-destructive">
                {errors.subcategory.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Attribute name"
              autoComplete="off"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="attribute_type">Attribute Type</Label>
            <Select
              value={attributeTypeValue}
              disabled={isEdit}
              onValueChange={(value: AttributeType) => {
                setValue('attribute_type', value, { shouldValidate: true })
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select attribute type" />
              </SelectTrigger>
              <SelectContent>
                {ATTRIBUTE_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.attribute_type && (
              <p className="text-sm text-destructive">
                {errors.attribute_type.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="description"
                  placeholder="Describe this attribute..."
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="required"
                checked={currentRequired}
                onCheckedChange={(checked: boolean) =>
                  setValue('required', checked)
                }
              />
              <Label htmlFor="required" className="cursor-pointer">
                Required
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="locked"
                checked={currentLocked}
                onCheckedChange={(checked: boolean) =>
                  setValue('locked', checked)
                }
              />
              <Label htmlFor="locked" className="cursor-pointer">
                Locked
              </Label>
            </div>
          </div>

          {currentAttributeType === 'DROPDOWN' && (
            <div className="space-y-2">
              <Label>Defined Values</Label>
              <Controller
                name="defined_values"
                control={control}
                render={({ field }) => (
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Type a value and press Enter..."
                  />
                )}
              />

              {errors.defined_values?.message && (
                <p className="text-sm text-destructive">
                  {errors.defined_values.message}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">
              {isEdit ? 'Save Changes' : 'Add Attribute'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
